/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import {
  FiUsers,
  FiMapPin,
  FiStar,
  FiShoppingBag,
  FiChevronDown,
  FiArrowUpRight
} from "react-icons/fi";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";
import { useUI } from "../../theme/ThemeProvider";
import { useNavigate } from "react-router-dom";

// --- Helper Functions ---
const parseDate = (val) => {
  if (!val) return null;
  const d = new Date(val.toString().replace(/(\d)(AM|PM)/, "$1 $2"));
  return isNaN(d.getTime()) ? null : d;
};

// دالة لتصفية البيانات بناءً على الفلتر المختار
const filterOrdersByTime = (orders, filter) => {
  const now = new Date();
  let startDate = new Date(0);
  if (filter === "1d") startDate = new Date(new Date().setHours(0, 0, 0, 0));
  else if (filter === "7d") startDate.setDate(now.getDate() - 7);
  else if (filter === "30d") startDate.setDate(now.getDate() - 30);
  else if (filter === "60d") startDate.setDate(now.getDate() - 60);
  else if (filter === "90d") startDate.setDate(now.getDate() - 90);
  else if (filter === "year") startDate.setDate(now.getDate() - 365);

  return orders.filter(o => {
    const d = parseDate(o.orderDate || o.OrderDate || o.createdAt);
    return filter === "all" ? true : (d && d >= startDate);
  });
};

// --- Sub-Component: SummaryCard (المعدل ليدعم الفلتر المستقل) ---
const SummaryCard = ({ title, icon: Icon, color, children, isDark, isArabic, onDetailClick, showFilter = true, filterValue, onFilterChange }) => (
  <div className={`rounded-[2rem] p-6 border flex flex-col h-[300px] relative transition-all duration-500 group
    ${isDark ? "bg-[#1e293b] border-[#334155] " : "bg-white border-gray-100 shadow-sm "}
  `}>
    <div className="flex justify-between items-start z-20 relative">
      <span className={`text-[10px] font-black uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}>
        {title}
      </span>
      <div className="p-2.5 rounded-xl transition-all duration-500  shadow-sm"
        style={{ backgroundColor: `${color}15`, color: color }}>
        <Icon size={18} />
      </div>
    </div>

    {/* Content Area */}
    <div className="flex-grow mt-2 overflow-hidden">
      {children}
    </div>

    {/* Footer Section: Details + Dropdown */}
    <div className="mt-auto pt-4 flex justify-between items-center z-30 relative w-full border-t border-gray-500/5">
      <button
        onClick={onDetailClick}
        className={`text-[10px] font-bold uppercase tracking-tight transition-colors flex items-center gap-1 ${isDark ? "text-gray-500 " : "text-gray-400 "}`}
      >
        {isArabic ? "التفاصيل" : "Details"}
        <FiArrowUpRight size={12} />
      </button>

      {showFilter && (
        <div className="relative flex items-center group/filter">
          <select
            value={filterValue}
            onChange={(e) => onFilterChange(e.target.value)}
            className={`appearance-none outline-none border-none pr-6 pl-2 py-1 rounded-lg text-[10px] font-bold cursor-pointer transition-all
              ${isDark ? "bg-slate-800 text-white " : "bg-gray-50 text-gray-500 "}`}
          >
            <option value="1d" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>{isArabic ? "اليوم" : "Today"}</option>
            <option value="7d" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>{isArabic ? "الأسبوع" : "This Week"}</option>
            <option value="30d" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>{isArabic ? "آخر 30 يوم" : "Last 30 Days"}</option>
            <option value="60d" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>{isArabic ? "آخر 60 يوم" : "Last 60 Days"}</option>
            <option value="90d" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>{isArabic ? "آخر 90 يوم" : "Last 90 Days"}</option>
            <option value="year" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>{isArabic ? "آخر عام" : "Last Year"}</option>
            <option value="all" style={{ backgroundColor: isDark ? '#1e293b' : '#fff', color: isDark ? '#fff' : '#000' }}>{isArabic ? "كل الفترة" : "All Time"}</option>
          </select>
          <FiChevronDown className={`pointer-events-none absolute ${isArabic ? 'left-1' : 'right-1'} text-current opacity-50`} size={11} />
        </div>
      )}
    </div>
  </div>
);

export default function CustomersSummary() {
  const { theme, lang } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const navigate = useNavigate();

  // فلاتر منفصلة لكل كرت
  const [custFilter, setCustFilter] = useState("all");
  const [prodFilter, setProdFilter] = useState("all");

  const [orders, setOrders] = useState([]);
  const [avgRating, setAvgRating] = useState(0);

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
  const tenantId = localStorage.getItem("tenantId");

  const PALETTE = { indigo: "#6366F1", pink: "#EC4899", gold: "#EAB308", blue: "#3B82F6" };

  useEffect(() => {
    const fetchData = async () => {
      if (!tenantId) return;
      try {
        const token = localStorage.getItem("authToken");
        const res = await fetch(`${baseUrl}/GetTenantOrders?tenantId=${tenantId}&statusId=-1`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" }
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) setOrders(data);
        }

        const revRes = await fetch(`${baseUrl}/Storereview/GetStoreReviews?tenantId=${tenantId}`);
        if (revRes.ok) {
          const reviews = await revRes.json();
          if (Array.isArray(reviews) && reviews.length > 0) {
            const sum = reviews.reduce((acc, curr) => acc + (curr.Rating || 0), 0);
            setAvgRating(Number((sum / reviews.length).toFixed(1)));
          }
        }
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, [baseUrl, tenantId]);

  // حساب بيانات الزبائن بناءً على فلتر الزبائن فقط
  const topCustomers = useMemo(() => {
    const filtered = filterOrdersByTime(orders, custFilter);
    const countMap = {};
    filtered.forEach(o => {
      const c = o.customerName || (isArabic ? "مجهول" : "Unknown");
      countMap[c] = (countMap[c] || 0) + 1;
    });
    return Object.entries(countMap).sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [orders, custFilter, isArabic]);

  // حساب بيانات المنتجات بناءً على فلتر المنتجات فقط
  const topProducts = useMemo(() => {
    const filtered = filterOrdersByTime(orders, prodFilter);
    const countMap = {};
    filtered.forEach(o => {
      (o.details || []).forEach(d => {
        const p = d.itemName || d.ItemName;
        if (p) countMap[p] = (countMap[p] || 0) + (d.quantity || 1);
      });
    });
    return Object.entries(countMap).sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [orders, prodFilter, isArabic]);

  // بيانات التشارت (تتبع فلتر الزبائن كمثال أو يمكن جعلها All Time)
  const chartData = useMemo(() => {
    const govCount = {};
    orders.forEach(o => {
      const g = o.city || (isArabic ? "أخرى" : "Other");
      govCount[g] = (govCount[g] || 0) + 1;
    });
    return Object.entries(govCount).map(([name, value]) => ({
      name,
      value: Math.round((value / (orders.length || 1)) * 100)
    })).sort((a, b) => b.value - a.value).slice(0, 5);
  }, [orders, isArabic]);

  const RankedList = ({ items, color }) => (
    <div className="flex flex-col gap-3 w-full px-1">
      {items.length > 0 ? items.map(([name, count], idx) => (
        <div key={idx} className="flex items-center justify-between group/item">
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-black opacity-20">0{idx + 1}</span>
            <span className={`text-[11px] font-bold truncate max-w-[130px] ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>{name}</span>
          </div>
          <span className="text-[10px] font-black px-2 py-0.5 rounded-md" style={{ backgroundColor: `${color}15`, color }}>{count}</span>
        </div>
      )) : <span className="text-center text-gray-400 text-[10px] w-full">{isArabic ? "لا توجد بيانات" : "No Data"}</span>}
    </div>
  );

  return (
    <div dir={isArabic ? "rtl" : "ltr"} className="w-full p-4 lg:p-10 space-y-10">
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">

        {/* كرت الزبائن بفلتر مستقل */}
        <SummaryCard
          title={isArabic ? "الزبائن الأكثر طلباً" : "Top Customers"}
          icon={FiUsers} color={PALETTE.indigo} isDark={isDark} isArabic={isArabic}
          filterValue={custFilter} onFilterChange={setCustFilter}
          onDetailClick={() => navigate('/admin-store/customers')}
        >
          <RankedList items={topCustomers} color={PALETTE.indigo} />
        </SummaryCard>

        {/* كرت المنتجات بفلتر مستقل */}
        <SummaryCard
          title={isArabic ? "المنتجات الأكثر مبيعاً" : "Top Products"}
          icon={FiShoppingBag} color={PALETTE.pink} isDark={isDark} isArabic={isArabic}
          filterValue={prodFilter} onFilterChange={setProdFilter}
          onDetailClick={() => navigate('/admin-store/reports/products')}
        >
          <RankedList items={topProducts} color={PALETTE.pink} />
        </SummaryCard>

        {/* كرت التقييم (بدون فلتر - All Time) */}
        <SummaryCard
          title={isArabic ? "تقييم المتجر" : "Store Rating"}
          icon={FiStar} color={PALETTE.gold} isDark={isDark} isArabic={isArabic}
          showFilter={false} onDetailClick={() => navigate('/admin-store/store-reviews')}
        >
          <div className="flex flex-col items-center justify-center">
            <span className={`text-5xl font-black tracking-tighter ${isDark ? 'text-white' : 'text-slate-800'}`}>{avgRating > 0 ? avgRating : "0.0"}</span>
            <div className="flex gap-1 mt-2 text-yellow-500">
              {[1, 2, 3, 4, 5].map(i => <FiStar key={i} size={14} fill={i <= Math.round(avgRating) ? "currentColor" : "none"} className={i <= Math.round(avgRating) ? "" : "text-gray-300"} />)}
            </div>
            <span className="text-[10px] mt-2 text-gray-400 font-medium">{isArabic ? "بناءً على تقييمات الزبائن" : "Based on reviews"}</span>
          </div>
        </SummaryCard>

      </section>

      {/* شارت توزيع المحافظات بتصميم مخصص لضمان الظهور على الموبايل */}
      <section className={`rounded-[2.5rem] p-6 md:p-8 border transition-all duration-500 ${isDark ? "bg-[#1e293b] border-[#334155]" : "bg-white border-gray-100 shadow-xl shadow-gray-200/40"}`}>
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 rounded-2xl bg-blue-500/10 text-blue-500 shrink-0"><FiMapPin size={20} /></div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-base md:text-lg font-black tracking-tight leading-tight truncate ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {isArabic ? "توزيع الطلبات حسب المحافظة" : "Order Distribution"}
            </h3>
          </div>
        </div>

        <div className="space-y-6">
          {chartData.length > 0 ? chartData.map((item, idx) => (
            <div key={idx} className="group">
              <div className="flex justify-between items-center mb-2 px-1">
                <span className={`text-[11px] font-black uppercase tracking-wide truncate ml-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                  {item.name}
                </span>
                <span className="text-[10px] font-black text-blue-500 bg-blue-500/10 px-2 py-0.5 rounded-md shrink-0">
                  {item.value}%
                </span>
              </div>
              <div className={`h-3 w-full rounded-full overflow-hidden p-[2px] ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`}>
                <div
                  className="h-full rounded-full transition-all duration-1000 ease-out"
                  style={{
                    width: `${item.value}%`,
                    backgroundColor: idx === 0 ? PALETTE.blue : `${PALETTE.blue}${Math.max(20, 90 - (idx * 15))}`
                  }}
                />
              </div>
            </div>
          )) : (
            <div className="flex items-center justify-center py-10 opacity-40 italic text-sm">
              {isArabic ? "لا توجد بيانات متاحة" : "No data available"}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}